import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Children } from '../../decorators/child.decorator';
import {
    duplicateIndexSetter,
    getFormattedRecordKey,
    INDEX_REGEXP,
} from '../../global/helpers';
import {
    closestByCond,
    focusInteractiveElement,
    hide,
    show,
    dispatchCustomEvent,
    setUnsafeInnerHTML,
} from '../../global/ui-helpers';
import { UIIcon } from '../icon/ui-icon';
import type {
    UIDuplicateFeedData,
    UIDuplicateLayout,
    UIDuplicateLayouts,
} from './ui-duplicate.type';
import type {
    HTMLInputSelectOrTextElement,
    NullOr,
} from '../../global/utils.type';
import type { ElementChildren, ElementConfig } from '../ui-element.type';
import styles from './ui-duplicate.css';
import type { TranslationLabels } from '../../types/labels.type';
import { Labels } from '../../global/labels';
import { UIDatalist } from '../datalist/ui-datalist';
import { UIField } from '../field/ui-field';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIDuplicate
 * @element ui-duplicate
 * @classdesc Represents a class for <code>ui-duplicate</code> element.
 * Adds UI for duplicate fields.
 * @property {string} template Template that is using for duplication
 * @property {("default" | "block" | "field")} [layout="default"]
 *  {@attr layout} Layout for ui-element element.
 *  {@desc default: default layout}
 *  {@desc block: places controls on new line}
 *  {@desc field: places controls inside ui-field content }
 * @property {string} [labelAdd="Add"] {@attr label-add} Label for add button.
 * @property {string} [labelRemove="Remove"]  {@attr label-remove} Label for remove button.
 * @property {number} [limit=0] {@attr limit} Max available duplicates.
 * @property {boolean} [rebuildIndexes=false] {@attr rebuild-indexes} Keeps order of indexes
 * in sorted way 0 to n. Always ordered sequence.
 * @property {boolean} [keepFirst=false] {@attr keep-first} Keeps first element not removable
 * @property {boolean} [showAddLabel=false] {@attr show-add-label} Show label for add buttons.
 * @property {boolean} [keepNames] {@attr keep-names} Do not add index to the name attribute value.
 * @property {NodeList<HTMLDivElement>} rows - Duplicated rows reference.
 * @property {HTMLTemplateElement} templateElement - Link to template block.
 * @fires event:row-added
 * @fires event:row-removed
 * @slot
 * @example
 * <ui-duplicate>
 *   <div>Content needs to be duplicated</div>
 * </ui-duplicate>
 */
@Component({ tag: 'ui-duplicate', styles })
export class UIDuplicate extends UIElement {
    /**
     * @type {{FIELD: string, BLOCK: string, DEFAULT: string}}
     */
    static readonly LAYOUTS: UIDuplicateLayouts = {
        DEFAULT: 'default',
        BLOCK: 'block',
        FIELD: 'field',
    };

    /**
     * Localization
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-duplicate', {
            add: 'Add',
            remove: 'Remove',
        });
    }

    @Attribute(String) layout?: UIDuplicateLayout;
    @Attribute(Number) limit: number = 0;
    @Attribute(String) labelAdd: TranslationLabels.Label =
        UIDuplicate.labels.add;
    @Attribute(String) labelRemove: TranslationLabels.Label =
        UIDuplicate.labels.remove;
    @Attribute(Boolean) rebuildIndexes?: boolean = false;
    @Attribute(Boolean) keepFirst?: boolean = false;
    @Attribute(Boolean) showAddLabel?: boolean = false;
    @Attribute(Boolean) keepNames?: boolean;

    @Children('.ui-duplicate__row') rows!: NodeListOf<HTMLElement>;
    @Child('script[type="text/x-template"]')
    templateElement?: HTMLScriptElement;

    get template(): string {
        if (this.templateElement) {
            return this.templateElement.innerHTML;
        }
        return this.innerHTML;
    }

    set template(htmlString: string) {
        let tpl = this.templateElement;
        if (!tpl) {
            tpl = this.createElement({
                tagName: 'script',
                attributes: {
                    type: 'text/x-template',
                },
            }) as HTMLScriptElement;
            if (this.firstElementChild) {
                this.insertBefore(tpl, this.firstElementChild);
            } else {
                this.appendChild(tpl);
            }
        }
        // No sanitization because the HTML is added to a template script tag
        // eslint-disable-next-line no-restricted-syntax
        tpl.innerHTML = htmlString;
    }

    /**
     * Replacer for index.
     * @param {string} matches
     * @param {string} $1
     * @param {string} $2
     * @param {string} $3
     * @returns {string}
     * @private
     */
    static indexDecrementReplacer(
        matches: string,
        $1: string,
        $2: string,
        $3: string
    ): string {
        let index = Number($2);
        let sign = '';
        if (index < 0) {
            index = Math.abs(index);
            sign = '-';
        }
        return --index > 0 ? ($1 || '') + sign + index + ($3 || '') : '';
    }

    /**
     * Decrements attribute index for duplicated row.
     * @param {Element} node
     * @param {string} attributeName
     * @private
     */
    static decrementAttributeIndex(node: Element, attributeName: string): void {
        node.setAttribute(
            attributeName,
            node
                .getAttribute(attributeName)!
                .replace(INDEX_REGEXP, UIDuplicate.indexDecrementReplacer)
        );
    }

    /**
     * Keeps aria-describedby references in sync when a referenced id gets
     * suffixed/decremented for duplication (e.g. a ui-tooltip linked to a
     * ui-field's label). Values can contain multiple space-separated ids,
     * only ids present in the map are replaced.
     * @param {HTMLElement} root
     * @param {Map<string, string>} idMap
     * @private
     */
    static updateAriaDescribedByReferences(
        root: HTMLElement,
        idMap: Map<string, string>
    ): void {
        if (!idMap.size) {
            return;
        }
        Array.from(
            root.querySelectorAll<HTMLElement>('[aria-describedby]')
        ).forEach((node) => {
            const value = node.getAttribute('aria-describedby')!;
            const updated = value
                .split(/\s+/)
                .map((id) => idMap.get(id) ?? id)
                .join(' ');
            if (updated !== value) {
                node.setAttribute('aria-describedby', updated);
            }
        });
    }

    /**
     * Build a new row from stored template.
     * @returns {this}
     */
    buildRow(): UIDuplicate {
        const controls: Array<ElementConfig> = [
            {
                tagName: 'button',
                attributes: {
                    class: 'ui-duplicate__button -add -iconed',
                    title: this.labelAdd,
                    type: 'button',
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'redesign-circle-plus',
                            bgcolor: UIIcon.colors.TURQUOISE,
                        },
                    },
                    (this.layout === UIDuplicate.LAYOUTS.BLOCK ||
                        this.showAddLabel) && {
                        tagName: 'span',
                        children: [this.labelAdd],
                    },
                ] as ElementChildren,
            },
            {
                tagName: 'button',
                attributes: {
                    class: 'ui-duplicate__button -remove -iconed',
                    title: this.labelRemove,
                    type: 'button',
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: 'redesign-circle-x-mark',
                            bgcolor: UIIcon.colors.ALERT_RED,
                        },
                    },
                    this.layout === UIDuplicate.LAYOUTS.BLOCK && {
                        tagName: 'span',
                        children: [this.labelRemove],
                    },
                ] as ElementChildren,
            },
        ];
        const index = this.getRowIndex();
        const rowElement = this.createElement({
            tagName: 'div',
            attributes: {
                'class': 'ui-duplicate__row',
                'data-index': index.toString(),
            },
            children: [
                {
                    tagName: 'div',
                    attributes: { class: 'ui-duplicate__content' },
                },
                {
                    tagName: 'div',
                    attributes: { class: 'ui-duplicate__control' },
                    children:
                        this.layout !== UIDuplicate.LAYOUTS.BLOCK
                            ? controls
                            : [
                                  {
                                      tagName: 'ui-field',
                                      classList: {
                                          '-remove': true,
                                      },
                                      children: [controls[1]],
                                  },
                                  { tagName: 'hr' },
                                  {
                                      tagName: 'ui-field',
                                      classList: {
                                          '-add': true,
                                      },
                                      children: [controls[0]],
                                  },
                              ],
                },
            ],
        });

        // Unfortunately we can't sanitize HTML here without breaking changes because HTML that can be slotted into ui-duplicate is too diverse
        const content = rowElement.querySelector<HTMLElement>(
            '.ui-duplicate__content'
        );
        if (content) {
            setUnsafeInnerHTML(content, this.template);
        }

        const field = rowElement.querySelector<UIField>('ui-field');
        if (field && this.layout === UIDuplicate.LAYOUTS.FIELD) {
            field.insertElements([
                rowElement.querySelector<HTMLElement>(
                    '.ui-duplicate__control'
                )!,
            ]);
        }

        if (index > 0) {
            const idMap = new Map<string, string>();
            Array.from(
                rowElement.querySelectorAll<HTMLElement>('[id]')
            ).forEach((node) => {
                const oldId = node.getAttribute('id');
                duplicateIndexSetter(node, 'id', index);
                const newId = node.getAttribute('id');
                if (oldId && newId && oldId !== newId) {
                    idMap.set(oldId, newId);
                }
            });
            UIDuplicate.updateAriaDescribedByReferences(rowElement, idMap);
            Array.from(
                rowElement.querySelectorAll<HTMLLabelElement>('label[for]')
            ).forEach((node) => duplicateIndexSetter(node, 'for', index));
            Array.from(
                rowElement.querySelectorAll<HTMLElement>('[name]')
            ).forEach((node) => {
                if (
                    node.getAttribute('name')?.includes('[]') ||
                    this.keepNames
                ) {
                    return;
                }
                duplicateIndexSetter(node, 'name', index);
            });
        }
        this.appendChild(rowElement);
        return this;
    }

    /**
     * Return parent row for element.
     * @param {ParentNode|Element} target
     * @returns {HTMLElement}
     */
    getRow(target: ParentNode | Element): HTMLElement {
        return closestByCond(target, (node) => {
            return (
                node instanceof Element &&
                node.classList.contains('ui-duplicate__row')
            );
        }) as HTMLElement;
    }

    /**
     * Create a new row.
     * @fires event:row-added
     * @returns {NullOr<HTMLDivElement>}
     */
    duplicateRow(): NullOr<HTMLDivElement> {
        if (this.limit > 0 && this.limit <= this.rows.length) {
            return null;
        }
        this.buildRow();
        dispatchCustomEvent(this, 'row-added', {
            index: this.getRowIndex() - 1,
        });
        focusInteractiveElement(this.lastElementChild as HTMLElement);
        return this.lastElementChild as HTMLDivElement;
    }

    /**
     * Return last row index.
     * @returns {number}
     */
    getRowIndex(): number {
        return this.lastElementChild &&
            this.lastElementChild.getAttribute('data-index')
            ? parseInt(this.lastElementChild.getAttribute('data-index')!) + 1
            : 0;
    }

    /**
     * Removes given row.
     * @fires event:row-removed
     * @param {HTMLElement} row
     */
    removeRow(row: HTMLElement): void {
        if (this.rebuildIndexes) {
            let next = row.nextElementSibling as HTMLElement | null;
            while (next) {
                this.decrementRowIndex(next);
                next = next.nextElementSibling as HTMLElement | null;
            }
        }
        const nextRowSibling = row.nextElementSibling;
        row.parentElement?.removeChild(row);
        dispatchCustomEvent(this, 'row-removed', {
            index: row.dataset.index,
        });
        focusInteractiveElement(
            (nextRowSibling || this.lastElementChild) as HTMLElement
        );
    }

    /**
     * Resets components to initial state
     */
    reset(): void {
        Array.from(this.rows).forEach((row) => this.removeChild(row));
        this.buildRow();
        focusInteractiveElement(this.lastElementChild as HTMLElement);
    }

    /**
     * Decrements indexes of duplicated for id, name, for attributes.
     * @param {HTMLElement} row
     * @private
     */
    private decrementRowIndex(row: HTMLElement): void {
        const index = Number(row.dataset.index) - 1;
        row.dataset.index = index.toString();
        const idMap = new Map<string, string>();
        Array.from(row.querySelectorAll<HTMLElement>('[id]')).forEach(
            (node) => {
                const oldId = node.getAttribute('id');
                UIDuplicate.decrementAttributeIndex(node, 'id');
                const newId = node.getAttribute('id');
                if (oldId && newId && oldId !== newId) {
                    idMap.set(oldId, newId);
                }
            }
        );
        UIDuplicate.updateAriaDescribedByReferences(row, idMap);
        Array.from(
            row.querySelectorAll<HTMLLabelElement>('label[for]')
        ).forEach((node) => UIDuplicate.decrementAttributeIndex(node, 'for'));
        Array.from(row.querySelectorAll<HTMLElement>('[name]')).forEach(
            (node) => {
                if (node.getAttribute('name')!.indexOf('[]') > -1) {
                    return;
                }
                UIDuplicate.decrementAttributeIndex(node, 'name');
            }
        );
    }

    /**
     * Shows remove button for all element except the last.
     * @returns {UIDuplicate}
     */
    syncRowsControls(): UIDuplicate {
        Array.from(this.rows).forEach((node: HTMLElement, index, list) => {
            const isLimitExceed = this.limit > 0 && this.limit <= list.length;
            const isLastRow = index + 1 === list.length;
            let canRemove =
                (!isLastRow ||
                    this.layout === UIDuplicate.LAYOUTS.BLOCK ||
                    isLimitExceed) &&
                list.length > 1 &&
                !(this.keepFirst && !index);
            let canDuplicate = isLastRow && !isLimitExceed;

            if (this.keepFirst && this.layout !== UIDuplicate.LAYOUTS.BLOCK) {
                canRemove = !!index;
                canDuplicate = !isLimitExceed && !index;
            }

            if (canDuplicate) {
                show(node.querySelector<HTMLElement>('.-add')!);
            } else {
                hide(node.querySelector<HTMLElement>('.-add')!);
            }

            if (canRemove) {
                show(node.querySelector<HTMLElement>('.-remove')!);
            } else {
                hide(node.querySelector<HTMLElement>('.-remove')!);
            }
        });
        return this;
    }

    /**
     * Fires callback when user click inside of component.
     * @param {Event} e
     * @private
     */
    private handleClickInside(e?: Event | CustomEvent): void {
        const button = closestByCond(e?.target as Element, (node) => {
            return (
                node instanceof Element &&
                node.classList.contains('ui-duplicate__button')
            );
        }) as HTMLButtonElement | null;
        if (!button) {
            return;
        }
        if (button.classList.contains('-add')) {
            this.duplicateRow();
        } else if (button.classList.contains('-remove')) {
            this.removeRow(this.getRow(button));
        }
        this.syncRowsControls();
    }

    /**
     * Sets the value of input. We set attributes instead of
     * properties due to pre-render mode.
     * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} input
     * @param {string|number|boolean} value
     * @private
     */
    private setInputValue(
        input: HTMLInputSelectOrTextElement,
        value: string | number | boolean
    ): void {
        if (input.type === 'radio') {
            if (input.value === value) {
                input.setAttribute('checked', '');
            }
        } else if (input.type === 'checkbox') {
            if (value) {
                input.setAttribute('checked', '');
            } else {
                input.removeAttribute('checked');
            }
        } else if (input.type === 'select-one') {
            const selectInput = input as HTMLSelectElement;
            for (let i = 0; i < selectInput.options.length; ++i) {
                if (selectInput.options[i].value === value) {
                    selectInput.options[i].setAttribute('selected', '');
                    break;
                }
            }
        } else {
            input.setAttribute('value', String(value));
        }
    }

    /**
     * Pre-fills rows with data.
     * @param {Array<object>} data
     */
    feedWithData(data: Array<UIDuplicateFeedData>): void {
        data.forEach((record: UIDuplicateFeedData) => {
            const row = this.duplicateRow();
            for (const key in record) {
                if (record.hasOwnProperty(key)) {
                    const inputs = row?.querySelectorAll(
                        '[name^="' + key + '" i]'
                    );
                    [].forEach.call(inputs, (input) => {
                        this.setInputValue(
                            input,
                            getFormattedRecordKey(record[key] as string)
                        );
                    });
                }
            }
        });
    }

    /**
     * Fetches template markup from element or from script[type=text/x-template].
     * element.
     * @returns {string}
     * @private
     */
    private fetchTemplate(): string {
        let html: string;
        const tpl = this.templateElement;
        if (!tpl) {
            html = Array.from(this.childNodes).reduce((str, node) => {
                if ((node as Element).localName !== 'ui-datalist') {
                    if (node.nodeType === Node.TEXT_NODE) {
                        str += node.textContent || '';
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                        str += (node as Element).outerHTML;
                    } else {
                        str += '';
                    }
                }
                return str;
            }, '');
        } else {
            html = tpl.innerHTML;
        }
        return html;
    }

    /**
     * Fetches the pre-filled data from JSON.
     * @returns {Array<object>}
     * @private
     */
    private fetchData(): Array<UIDuplicateFeedData> {
        const dl = this.querySelector<UIDatalist>('ui-datalist');
        const data: Array<UIDuplicateFeedData> = [];
        if (dl) {
            Array.from(dl.getOptions()).forEach((opt) => {
                data.push(dl.getOptionAttributes(opt, true));
            });
        }
        return data;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const templateHTML = this.fetchTemplate();
        const dl = this.querySelector<UIDatalist>('ui-datalist');
        const data = this.fetchData();

        // Reset default HTML
        this.innerHTML = '';
        this.template = templateHTML;
        if (dl) {
            this.appendChild(dl);
        }

        if (data.length > 0) {
            this.feedWithData(data);
        } else {
            this.buildRow();
        }
        this.syncRowsControls();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.updateElement({
            events: {
                click: this.handleClickInside.bind(this),
            },
        });
    }
}
